import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { sharedApi, DEVICE_ID_HEADERS } from "@/lib/api";
import { gaEvent } from "@/lib/ga";

const AuthCtx = createContext(null);

/** Read a JSON value from localStorage, treating the literal string "undefined"/"null" as absent. */
function readLS(key) {
  const v = localStorage.getItem(key);
  if (!v || v === "undefined" || v === "null") return null;
  try { return JSON.parse(v); } catch { return null; }
}

/** Read a raw token, treating "undefined"/"null" as absent (guards against past broken writes). */
function readToken() {
  const v = localStorage.getItem("allsale_token");
  if (!v || v === "undefined" || v === "null") return null;
  return v;
}

async function fetchAdminStatus(userId) {
  try {
    const { data } = await sharedApi.get("/auth/is-admin");
    return {
      is_admin: !!data?.is_admin,
      role: data?.role || null,
      user_id: userId || null,
    };
  } catch {
    return { is_admin: false, role: null, user_id: userId || null };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readLS("allsale_user"));
  const [token, setToken] = useState(() => readToken());
  const [admin, setAdmin] = useState(() => readLS("allsale_admin"));
  const [ready, setReady] = useState(false);

  // Refetch the admin flag whenever we have a signed-in user AND either
  // (a) we have no admin cache, or (b) the cached flag was set for a
  // different user (stale from a prior session on the same browser).
  // This is the fix for the bug where a regular user saw the "Admin"
  // pill after signing in on a device that had previously hosted an
  // admin session (or the cache was seeded via an unrelated account).
  useEffect(() => {
    const currentUserId = user?.id || null;
    const stale = admin && admin.user_id !== currentUserId;
    if (token && currentUserId && (!admin || stale)) {
      if (stale) setAdmin(null); // hide the pill immediately while refetching
      fetchAdminStatus(currentUserId).then((s) => {
        setAdmin(s);
        localStorage.setItem("allsale_admin", JSON.stringify(s));
      });
    }
    if (!token && admin) {
      setAdmin(null);
      localStorage.removeItem("allsale_admin");
    }
    setReady(true);
  }, [token, user?.id]);

  /** Persist a successful token+user pair. Only called with a truthy access_token. */
  const applySession = async (accessToken, userObj) => {
    localStorage.setItem("allsale_token", accessToken);
    localStorage.setItem("allsale_user", JSON.stringify(userObj));
    setToken(accessToken);
    setUser(userObj);
    // Iter-260: tag admin cache with the current user_id so a stale
    // cache from a prior session on this browser can never win.
    const s = await fetchAdminStatus(userObj?.id);
    localStorage.setItem("allsale_admin", JSON.stringify(s));
    setAdmin(s);
    return userObj;
  };

  /**
   * Step 1 of login. Returns either:
   *   { kind: "ok", user }               — direct login (no 2FA)
   *   { kind: "otp", ephemeralToken, channel, maskedDestination, ttlMinutes }
   */
  const login = async (email, password) => {
    // Iter-291 §2 — admin-bridge login: include `site: "classifieds"` so
    // the backend can validate a sub-admin's site scope on this front.
    // Regular members: the field is ignored — zero behaviour change.
    const { data } = await sharedApi.post("/auth/login", { email, password, site: "classifieds" });
    if (data?.access_token) {
      const u = await applySession(data.access_token, data.user);
      gaEvent("login", { method: "password" });
      return { kind: "ok", user: u };
    }
    if (data?.requires_2fa && data?.ephemeral_token) {
      return {
        kind: "otp",
        ephemeralToken: data.ephemeral_token,
        channel: data.channel || "email",
        maskedDestination: data.masked_destination || data.masked_email || "",
        ttlMinutes: data.ttl_minutes || 5,
      };
    }
    // Defensive: unknown shape — force the caller to treat as failure.
    throw new Error("Unexpected login response");
  };

  /** Step 2 of login: verify the OTP code the user typed. */
  const verifyOtp = async (ephemeralToken, code) => {
    const { data } = await sharedApi.post("/auth/2fa/login-verify", {
      ephemeral_token: ephemeralToken,
      code,
    });
    if (!data?.access_token) throw new Error("Verification failed");
    const u = await applySession(data.access_token, data.user);
    gaEvent("login", { method: "password" });
    return u;
  };

  /** Ask the backend to resend the OTP for the current challenge. */
  const resendOtp = async (ephemeralToken) => {
    const { data } = await sharedApi.post("/auth/2fa/resend", { ephemeral_token: ephemeralToken });
    return data;
  };

  /**
   * Iter-274d W5: Redeem an Emergent Google SSO session id. Returns the
   * same `AuthResponse` shape as email login.
   */
  const applyGoogleSession = async (sessionId) => {
    const { data } = await sharedApi.post("/auth/google-session", { session_id: sessionId }, DEVICE_ID_HEADERS());
    if (!data?.access_token) throw new Error("Google sign-in failed");
    const u = await applySession(data.access_token, data.user);
    gaEvent(data?.is_new_user ? "sign_up" : "login", { method: "google" });
    return u;
  };

  /**
   * Iter-297: AllSale-branded Google flow. The token is delivered in the
   * URL fragment on /auth/google/done — GoogleDonePage hydrates
   * /auth/me and then hands us the pair. Just persist and hydrate the
   * admin flag identically to email login.
   */
  const setSessionFromToken = async (accessToken, userObj) => {
    return applySession(accessToken, userObj);
  };

  /**
   * Iter-274 W6 phone-login step 1: request an SMS code for `phone`.
   * Backend replies 503 until Twilio creds land; the caller surfaces the
   * status verbatim so the user sees "SMS sign-in coming soon".
   */
  const requestPhoneLogin = async (phone) => {
    const { data } = await sharedApi.post("/auth/phone-login/request", { phone });
    return data; // { sent, masked_phone }
  };

  /** Iter-274 W6 phone-login step 2: confirm the 6-digit code. */
  const confirmPhoneLogin = async (phone, code) => {
    const { data } = await sharedApi.post("/auth/phone-login/confirm", { phone, code });
    if (!data?.access_token) throw new Error("Phone sign-in failed");
    return applySession(data.access_token, data.user);
  };

  /**
   * Iter-274 W6 retroactive Business switch (and general non-sensitive
   * profile edit). Per bulletin ITER274E W4: PATCH /auth/me must NOT
   * trigger email-OTP for full_name / picture / account_type / business.*
   * fields — the backend already returns the fresh user, so we just
   * refresh local state.
   */
  const patchMe = async (patch) => {
    const { data } = await sharedApi.patch("/auth/me", patch);
    if (data) {
      const merged = { ...(user || {}), ...data };
      localStorage.setItem("allsale_user", JSON.stringify(merged));
      setUser(merged);
    }
    return data;
  };

  const register = async (payload) => {
    const { data } = await sharedApi.post(
      "/auth/register",
      {
        country: "NZ",
        signup_source: "classifieds",
        ...payload,
      },
      DEVICE_ID_HEADERS(),
    );
    if (!data?.access_token) throw new Error("Registration failed");
    const u = await applySession(data.access_token, data.user);
    gaEvent("sign_up", { method: "password" });
    return u;
  };

  const logout = () => {
    localStorage.removeItem("allsale_token");
    localStorage.removeItem("allsale_user");
    localStorage.removeItem("allsale_admin");
    setToken(null);
    setUser(null);
    setAdmin(null);
  };

  const value = useMemo(
    () => ({
      user, token, ready, admin, isAdmin: !!admin?.is_admin,
      login, verifyOtp, resendOtp, register, logout,
      applyGoogleSession, setSessionFromToken, requestPhoneLogin, confirmPhoneLogin, patchMe,
    }),
    [user, token, ready, admin],
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}

export function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  // Wait for the auth provider to finish loading before making a routing decision.
  if (!ready) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center text-slate-400">
        <div className="inline-block w-6 h-6 rounded-full border-2 border-slate-300 border-t-[color:var(--brand-blue)] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
}
